import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import apiClient from "./utils/apiClient";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import SignUpDialog from "./SignUpDialog";
import { useAuthStore } from "./store/authStore";
import { useOptInStore } from "./store/optInStore";
import { useRoleStore } from "./store/roleStore";
import { useEvaluationStore } from "./store/evaluationStore";
import { jwtDecode } from 'jwt-decode';


const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaTicket, setMfaTicket] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [mfaErrorMessage, setMfaErrorMessage] = useState("");
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [openSignUp, setOpenSignUp] = useState(false);
  const [openMfaDialog, setOpenMfaDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [openMfaVerificationDialog, setOpenMfaVerificationDialog] = useState(false);
  const [showTOTPSetupPrompt, setShowTOTPSetupPrompt] = useState(false);
  const [userAfterLogin, setUserAfterLogin] = useState(null);
  const [step, setStep] = useState("email");          // "email" | "password"
  const [resolving, setResolving] = useState(false);  // POST /v1/auth/resolve in flight
  const mfaInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const navigate = useNavigate();
  const authLogin = useAuthStore(state => state.login);
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const { fetchLicenseOptIn } = useOptInStore();
  const fetchRoles = useRoleStore.getState().fetchRoles;

  useEffect(() => {
    if (openMfaVerificationDialog) {
      setTimeout(() => {
        if (mfaInputRef.current) {
          mfaInputRef.current.focus();
        }
      }, 0);
    }
  }, [openMfaVerificationDialog]);

  useEffect(() => {
    if (step === "password" && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [step]);

  const handleSuccessfulAuth = async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      setErrorMessage("User info not available after login.");
      return;
    }
    const userEmail = user.email || user.id;
    if (!userEmail) {
      setErrorMessage("User email not available after login.");
      return;
    }
    localStorage.setItem('userEmail', userEmail);

    // Fetch all roles for roleStore once after login
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const token = useAuthStore.getState().token;
    if (isAuthenticated && token && typeof token === 'string' && token.trim() && token !== 'null' && token !== 'undefined') {
      await fetchRoles(isAuthenticated, token);
    }

    // Only show TOTP setup if totp_enabled is false
    if (user.totp_enabled === false) {
      setUserAfterLogin(user); // Only for dialog display
      setShowTOTPSetupPrompt(true);
      return;
    }

    try {
      const hasAcceptedInBackend = await fetchLicenseOptIn(userEmail);
      if (hasAcceptedInBackend) {
        navigate(await landingPathAfterLogin(user));
      } else {
        navigate("/license");
      }
    } catch (error) {
      console.error("Error checking license agreement:", error);
      navigate("/license");
    }
  };

  // Land Admin/InternalUser reviewers straight on Evaluations when they have
  // pending (Admin-flagged, not-yet-done-by-them) reports waiting — otherwise
  // the usual Reports landing page.
  const landingPathAfterLogin = async (loggedInUser) => {
    const roles = Array.isArray(loggedInUser.roles)
      ? loggedInUser.roles
      : typeof loggedInUser.roles === "string" && loggedInUser.roles
        ? [loggedInUser.roles]
        : [];
    const canSeeEvaluations = roles.includes("Admin") || roles.includes("InternalUser");
    if (canSeeEvaluations) {
      try {
        const rows = await useEvaluationStore.getState().fetchAll(true);
        const hasPending = (rows || []).some((r) => r.run_evaluate && !r.completed_by_me);
        if (hasPending) return "/evaluations";
      } catch (e) {
        console.warn("Failed to check pending evaluations after login:", e);
      }
    }
    return `/report-list/${loggedInUser.id}`;
  };

  const handleResolveEmail = async () => {
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    setResolving(true);
    setErrorMessage("");
    try {
      const response = await apiClient.post("/auth/resolve", { email: trimmed });
      if (response?.mode === "sso" && response?.redirect_url) {
        // Hand off to the customer's IdP. No further UI here.
        window.location.href = response.redirect_url;
        return;
      }
      // Default: continue to password step.
      setEmail(trimmed);
      setStep("password");
    } catch (err) {
      console.error("Auth resolve error:", err);
      // Fail open to password mode rather than blocking login on a transient
      // /auth/resolve outage — local users can still sign in.
      setEmail(trimmed);
      setStep("password");
    } finally {
      setResolving(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (step === "email") {
      await handleResolveEmail();
      return;
    }
    try {
      const response = await authLogin({ username: email, password });
      if (response && response.mfa_required) {
        setMfaTicket(response.mfa_ticket || "");
        setOpenMfaVerificationDialog(true);
        return;
      }
      apiClient.setToken(useAuthStore.getState().token?.replace('Bearer ', ''));
      window.dispatchEvent(new Event('userActivity'));
      setErrorMessage("");
      await handleSuccessfulAuth();
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Invalid email or password. Please try again.");
    }
  };

  const handleResetToEmail = () => {
    setStep("email");
    setPassword("");
    setErrorMessage("");
  };

  const handleMfaLogin = async (event) => {
    event.preventDefault();
    try {
      const response = await apiClient.post("/mfa/login", {
        username: email,
        code: mfaCode,
        mfa_ticket: mfaTicket,
      });
  
      if (response.access_token) {
        const token = response.access_token;
        apiClient.setToken(token);
  
        let userFromToken = {};
        try {
          const decoded = jwtDecode(token); // ✅ correct usage
          userFromToken = decoded.user || {};
        } catch (e) {
          console.error("JWT decoding failed", e);
        }
  
        useAuthStore.setState({
          token,
          user: userFromToken,
          isAuthenticated: true,
        });
  
        window.dispatchEvent(new Event("userActivity"));
        setErrorMessage("");
        setMfaErrorMessage("");
        setOpenMfaVerificationDialog(false);
        await handleSuccessfulAuth(); // Ensures global roles are fetched and navigation is handled
        return;
      }
    } catch (error) {
      setMfaErrorMessage("Invalid MFA code. Please try again.");
    }
  };
  

  const handleSetupMfa = async (user) => {
    try {
      const userId = user?.email || user?.id;
      const response = await apiClient.post("/mfa/setup", { id: userId });
      if (response.otp_auth_url) {
        setQrCodeUrl(response.otp_auth_url);
        setOpenMfaDialog(true);
      }
    } catch (error) {
      setErrorMessage("Failed to set up MFA. Please try again.");
    }
  };

  const handleMfaKeyDown = (event) => {
    if (event.key === "Enter") {
      handleMfaLogin(event);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#b3e5fc",
        backgroundImage: "url(/freeze_data.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        paddingTop: "50px",
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          style={{
            margin: "20px auto",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          }}
          elevation={10}
        >
          <Box
            style={{
              backgroundColor: "rgb(47, 63, 92)",
              color: "white",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <img
              src="/Metalytics-Logo_light.webp"
              alt="Metalytics Logo"
              style={{ height: "40px" }}
            />
            <Typography component="h1" variant="h6">
              Continuous MFA Login
            </Typography>
          </Box>

          <Box
            style={{
              padding: "20px",
              backgroundColor: "rgb(252, 254, 255)",
            }}
          >
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <Box component="form" onSubmit={handleLogin} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={step === "password" || resolving}
                slotProps={{
                  htmlInput: {
                    "data-testid": "login-email",
                  },
                }}
              />
              {step === "password" && (
                <Box sx={{ mt: 0.5, mb: 0.5, textAlign: "right" }}>
                  <Link
                    type="button"
                    component="button"
                    variant="caption"
                    onClick={handleResetToEmail}
                  >
                    Use a different email
                  </Link>
                </Box>
              )}
              {step === "password" && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  inputRef={passwordInputRef}
                  onChange={(e) => setPassword(e.target.value)}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "login-password",
                    },
                  }}
                />
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={resolving}
                sx={{ mt: 3, mb: 2, backgroundColor: "rgb(47, 63, 92)" }}
                data-testid="login-submit"
              >
                {step === "email" ? (resolving ? "Checking…" : "Continue") : "Login"}
              </Button>
              <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                {step === "password" && (
                  <Grid size={{ xs: 'auto' }}>
                    <Link
                      type="button"
                      component="button"
                      variant="body2"
                      onClick={() => setOpenForgotPassword(true)}
                    >
                      Forgot password?
                    </Link>
                  </Grid>
                )}
                <Grid>
                  <Link
                    type="button"
                    component="button"
                    variant="body2"
                    onClick={() => setOpenSignUp(true)}
                  >
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog open={openMfaDialog} onClose={() => setOpenMfaDialog(false)}>
        <DialogTitle>MFA Setup</DialogTitle>
        <DialogContent>
          <Typography>
            Scan the QR code below with your authenticator app:
          </Typography>
          {qrCodeUrl && (
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
                qrCodeUrl
              )}&size=200x200`}
              alt="QR Code"
              style={{ marginTop: "16px" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMfaDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMfaVerificationDialog} onClose={() => setOpenMfaVerificationDialog(false)}>
        <DialogTitle>MFA Verification</DialogTitle>
        <DialogContent>
          <Typography>Enter the code from your authenticator app:</Typography>
          {mfaErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mfaErrorMessage}
            </Alert>
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            id="mfaCode"
            label="MFA Code"
            name="mfaCode"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            inputRef={mfaInputRef}
            onKeyDown={handleMfaKeyDown}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMfaLogin}>Verify</Button>
          <Button onClick={() => setOpenMfaVerificationDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showTOTPSetupPrompt} onClose={() => setShowTOTPSetupPrompt(false)}>
        <DialogTitle>Set Up Two-Factor Authentication (TOTP)</DialogTitle>
        <DialogContent>
          <Typography>
            For enhanced security, please set up TOTP (Time-based One-Time Password) authentication. You can skip this step for now, but we recommend enabling it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              setShowTOTPSetupPrompt(false);
              await handleSetupMfa(userAfterLogin); // Only for dialog, not Zustand update
            }}
            color="primary"
          >
            Set Up TOTP
          </Button>
          <Button
            onClick={async () => {
              setShowTOTPSetupPrompt(false);
              // Only update totp_enabled on the current user in Zustand
              useAuthStore.setState(state => ({
                user: { ...state.user, totp_enabled: true }
              }));
              await handleSuccessfulAuth();
            }}
            color="secondary"
          >
            Skip for now
          </Button>
        </DialogActions>
      </Dialog>

      <ForgotPasswordDialog
        open={openForgotPassword}
        onClose={() => setOpenForgotPassword(false)}
      />
      <SignUpDialog
        open={openSignUp}
        onClose={() => setOpenSignUp(false)}
        validatePassword={(password) => {
          const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/;
          return passwordRegex.test(password)
            ? ""
            : "Password must be at least 8 characters long, include at least one uppercase letter, one lowercase letter, and one special character";
        }}
      />
    </div>
  );
};

export default Login;
